import React from 'react';
import {
  ErrorState, FilterChip, KpiCard, SectionCard, SkeletonCard, SkeletonText, TrendChart,
} from '@parsina/framework';
import { C, chartPalette } from '@parsina/framework/tokens';
import { Activity, Coins, DollarSign, Layers, Percent, TrendingUp } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { formatUsd, EMPTY, isIrtCredit } from '../../lib/format';
import { num, OWNER_TYPES } from '../../lib/aiPricingView';
import BreakdownList from '../../components/BreakdownList';
import ChainStepper from './ChainStepper';
import { shortModelId } from './ModelShareChips';
import { Code } from './shared';

const TOP_N = 8;

/**
 * View «نمای کلی» — Archetype A (cockpit) inside the pricing screen.
 *
 * ③ six KPIs · ⑤ P2: the cost-vs-revenue chart and the chain card in main,
 * the two breakdowns in the rail.
 *
 * Every figure the BFF returns as `null` renders «—». A zero here is a claim
 * about the business — «the platform earned nothing» — and the service says
 * NULL precisely when it cannot make that claim (design §7.2).
 *
 * The overview query itself is owned by the container, because its `alerts`
 * feed the alert zone on every view; this component only renders it.
 */
export default function OverviewView({
  overview, loading, error, onRetry, window, view, go,
}) {
  const { t, formatNumber, formatPercent, formatDate } = useTranslation();

  const ownerChips = (
    <div
      role="group"
      aria-label={t('aiPricing.overview.owner.label')}
      className="mb-4 flex flex-wrap items-center gap-1.5"
      data-testid="ai-overview-owner"
    >
      {['', ...OWNER_TYPES].map((value) => (
        <FilterChip
          key={value || 'all'}
          label={t(`aiPricing.overview.owner.${value || 'all'}`)}
          active={(view.owner || '') === value}
          onClick={() => go({ owner: value })}
          data-testid={`ai-overview-owner-${value || 'all'}`}
        />
      ))}
    </div>
  );

  if (loading) return <>{ownerChips}<OverviewSkeleton label={t('aiPricing.overview.loading')} /></>;

  if (error || !overview) {
    return (
      <>
        {ownerChips}
        <ErrorState
          title={t('aiPricing.overview.error')}
          description={error || t('errors.region.noAnswer')}
          onRetry={onRetry}
          retryLabel={t('common.actions.retry')}
          data-testid="ai-overview-error"
        />
      </>
    );
  }

  const o = overview;
  const usd = (v) => formatUsd(v, { formatNumber, t });
  const pct = (v) => (num(v) == null ? EMPTY : formatPercent(num(v)));

  const kpis = [
    { key: 'cost', label: t('aiPricing.overview.kpi.cost'), value: usd(o.totalCostUsd), unit: t('common.units.usd'), icon: <DollarSign size={20} color={C.navy} />, bg: C.navyLight },
    // Toman is a plain number + the unit word — NOT `formatMoney`, which takes
    // rials and would show a tenth of the figure.
    // Only where a credit is priced in toman (PM-0211): a USD deployment has no
    // FX, so no toman cost exists to show.
    isIrtCredit(o.creditCurrency) ? { key: 'costToman', label: t('aiPricing.overview.kpi.costToman'), value: num(o.totalCostToman) == null ? EMPTY : formatNumber(num(o.totalCostToman), { maximumFractionDigits: 0 }), unit: t('common.units.toman'), icon: <Coins size={20} color={C.teal} />, bg: C.tealLight } : null,
    { key: 'revenue', label: t('aiPricing.overview.kpi.revenue'), value: num(o.revenueParseh) == null ? EMPTY : formatNumber(num(o.revenueParseh), { maximumFractionDigits: 0 }), unit: t('common.units.parseh'), icon: <Coins size={20} color={C.green} />, bg: C.greenLight },
    { key: 'blended', label: t('aiPricing.overview.kpi.blended'), hint: t('aiPricing.overview.kpi.blendedHint'), value: usd(o.blendedCostPerParsehUsd), unit: t('common.units.usd'), icon: <Layers size={20} color={C.navy} />, bg: C.navyLight },
    { key: 'margin', label: t('aiPricing.overview.kpi.margin'), value: pct(o.blendedMarginPct), icon: <Percent size={20} color={num(o.blendedMarginPct) != null && num(o.blendedMarginPct) < 0 ? C.red : C.green} />, bg: num(o.blendedMarginPct) != null && num(o.blendedMarginPct) < 0 ? C.redLight : C.greenLight },
    { key: 'invocations', label: t('aiPricing.overview.kpi.invocations'), sub: t('aiPricing.overview.kpi.invocationsSub', { calls: o.calls ?? 0 }), value: formatNumber(o.invocations ?? 0), icon: <Activity size={20} color={C.amber} />, bg: C.amberLight },
  ].filter(Boolean);

  const daily = o.daily || [];
  const costReadings = daily.map((d) => ({ id: d.day, value: d.costUsd, day: d.day }));
  const revenueReadings = daily.map((d) => ({ id: d.day, value: d.revenueUsd, day: d.day }));
  const formatX = (r) => formatDate(r.day, 'dayMonth');
  const formatAxis = (v) => formatNumber(v, { maximumFractionDigits: 2 });

  const breakdown = (items, hrefOf, labelOf) => {
    const list = items || [];
    const top = list.slice(0, TOP_N);
    const rest = list.slice(TOP_N);
    const rows = top.map((x) => ({
      key: x.key,
      label: labelOf ? labelOf(x) : x.label || x.key,
      title: x.label || x.key,
      value: usd(x.costUsd),
      percent: Math.max(0, Math.min(100, Math.round(num(x.sharePct) ?? 0))),
      tone: C.teal,
      extra: formatNumber(x.count ?? 0),
      href: hrefOf(x),
    }));
    if (rest.length) {
      // «سایر» is a DISPLAY aggregate of what the top eight leave out; the
      // figures are the BFF's, added for the row rather than derived anew.
      const restCost = rest.reduce((s, x) => s + (num(x.costUsd) ?? 0), 0);
      const restShare = rest.reduce((s, x) => s + (num(x.sharePct) ?? 0), 0);
      rows.push({
        key: '__other',
        label: t('aiPricing.overview.other'),
        value: usd(restCost),
        percent: Math.max(0, Math.min(100, Math.round(restShare))),
        tone: C.textMuted,
        extra: formatNumber(rest.reduce((s, x) => s + (x.count || 0), 0)),
      });
    }
    return rows;
  };

  const byService = breakdown(o.byService, (x) => `/ai-pricing?view=services&service=${encodeURIComponent(x.key)}&w=${view.w}`);
  const byModel = breakdown(o.byModel, (x) => `/ai-pricing?view=models&model=${encodeURIComponent(x.key)}&w=${view.w}`, (x) => <Code>{shortModelId(x.label || x.key)}</Code>);

  return (
    <div data-testid="ai-overview" data-state="content">
      {ownerChips}

      <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6" data-testid="ai-overview-kpis">
        {kpis.map((kpi) => (
          <div key={kpi.key} title={kpi.hint} data-testid={`ai-overview-kpi-${kpi.key}`}>
            <KpiCard
              minWidth={0}
              label={(
                <span className="block">
                  <span className="block">{kpi.label}</span>
                  {kpi.sub ? <span className="mt-0.5 block text-[10.5px] text-muted-foreground">{kpi.sub}</span> : null}
                </span>
              )}
              value={kpi.value}
              unit={kpi.unit}
              icon={kpi.icon}
              bg={kpi.bg}
            />
          </div>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-5">
          <SectionCard
            title={t('aiPricing.overview.chart.title')}
            sub={t('aiPricing.overview.chart.sub')}
            icon={TrendingUp}
            data-testid="ai-overview-chart"
          >
            {/* Two single-series charts on one x-domain, not one chart with two
                axes: the kit's `TrendChart` draws ONE series, and money against
                money on separate scales would make the comparison a lie. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                { key: 'cost', label: t('aiPricing.overview.chart.cost'), readings: costReadings, color: chartPalette[2] },
                { key: 'revenue', label: t('aiPricing.overview.chart.revenue'), readings: revenueReadings, color: chartPalette[1] },
              ].map((series) => (
                <div key={series.key} data-testid={`ai-overview-chart-${series.key}`}>
                  <div className="mb-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: series.color }} aria-hidden />
                    {series.label}
                  </div>
                  <TrendChart
                    readings={series.readings}
                    color={series.color}
                    height={150}
                    formatX={formatX}
                    formatValue={formatAxis}
                    emptyLabel={t('aiPricing.overview.chart.empty')}
                  />
                </div>
              ))}
            </div>
            {/* The chart is decorative by the kit's contract; this line is its
                accessible equivalent. */}
            <p className="mt-2 text-xs text-muted-foreground">
              {t('aiPricing.overview.chart.summary', {
                days: daily.length,
                cost: usd(o.totalCostUsd),
                revenue: usd(o.revenueUsd),
              })}
            </p>
          </SectionCard>

          {o.mostExpensiveService ? (
            <ChainStepper
              serviceKey={o.mostExpensiveService}
              window={window}
              href={`/ai-pricing?view=services&service=${encodeURIComponent(o.mostExpensiveService)}&w=${view.w}`}
            />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <SectionCard title={t('aiPricing.overview.byService.title')} sub={t('aiPricing.overview.byService.sub')} icon={Layers}>
            {byService.length ? (
              <BreakdownList rows={byService} data-testid="ai-overview-by-service" />
            ) : (
              <p className="py-2 text-sm text-muted-foreground" data-testid="ai-overview-by-service-empty">
                {t('aiPricing.overview.breakdownEmpty')}
              </p>
            )}
          </SectionCard>
          <SectionCard title={t('aiPricing.overview.byModel.title')} sub={t('aiPricing.overview.byModel.sub')} icon={Layers}>
            {byModel.length ? (
              <BreakdownList rows={byModel} data-testid="ai-overview-by-model" />
            ) : (
              <p className="py-2 text-sm text-muted-foreground" data-testid="ai-overview-by-model-empty">
                {t('aiPricing.overview.breakdownEmpty')}
              </p>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

/** Six KPI blocks in the real grid, then the P2 shape — nothing jumps on arrival. */
function OverviewSkeleton({ label }) {
  return (
    <div className="space-y-5" data-testid="ai-overview-loading" data-state="loading" role="status" aria-busy="true" aria-label={label}>
      <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <SkeletonText width="60%" height={22} />
            <div className="mt-3"><SkeletonText width="80%" height={11} /></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="space-y-5"><SkeletonCard /><SkeletonCard /></div>
        <div className="space-y-5"><SkeletonCard /><SkeletonCard /></div>
      </div>
    </div>
  );
}
